import { useCallback, useEffect, useMemo, useState } from "react";

import { AlertDetail, AlertImportance, AlertTableRow, AlertType } from "@models/Alertas";
import { AlertsService } from "@services/AlertasService";
import { Description } from "@shared/utils/DescriptionsHelper";
import Utils from "@utils/Utils";

export interface AlertasListItem {
    id: string;
    description: string;
    typeLabel: string;
    importanceLabel: string;
    buildingName: string;
    shiftLabel: string;
    scheduleLabel: string;
    countLabel: string;
    dateLabel: string;
    row: AlertTableRow;
}

interface UseAlertasListViewModelProps {
    orgID: string;
    timezone: string;
    service: AlertsService;
}

const normalizeSearch = (value: string) => value.toLowerCase().replace(/\s/g, "");

const safeText = (value: unknown, fallback: string = "N/D".localized()): string => {
    if (typeof value === "string" && value.trim().length > 0) {
        return value;
    }

    if (typeof value === "number" && Number.isFinite(value)) {
        return String(value);
    }

    return fallback;
};

/** Kiosk spoof alerts are about one fichada, not a shift: they have no shift, schedule or count. */
export const isKioskEvidenceSpoofAlert = (type: AlertType | any): boolean => type === AlertType.kioskEvidenceSpoof;

export const kioskEvidenceSpoofShiftLabel = (): string => "Fichada de kiosco".localized();

const notApplicableLabel = "—";

export const typeToLabel = (type: AlertType | any): string => {
    switch (type) {
        case AlertType.shiftCountControl:
            return "Control de turnos".localized();
        case AlertType.kioskEvidenceSpoof:
            return "Posible suplantación en kiosco".localized();
        default:
            return String(type ?? "Desconocido".localized());
    }
};

const importanceToLabel = (importance: AlertImportance): string => {
    switch (importance) {
        case AlertImportance.high:
            return "Alta".localized();
        case AlertImportance.medium:
            return "Media".localized();
        case AlertImportance.low:
            return "Baja".localized();
        default:
            return String(importance ?? "N/D".localized());
    }
};

export const mapAlertRow = (row: AlertTableRow, timezone: string): AlertasListItem => {
    const rawRow = row as AlertTableRow & {
        hourFrom?: string;
        hourTo?: string;
        shiftRecipeName?: string;
    };
    const buildingName = safeText(row.buildingName);
    const shiftLabel = safeText(rawRow.shiftRecipeName ?? row.shiftInstanceID);
    const hourFrom = safeText(row.shiftInstance?.hourFrom ?? rawRow.hourFrom, "");
    const hourTo = safeText(row.shiftInstance?.hourTo ?? rawRow.hourTo, "");
    const scheduleLabel = hourFrom && hourTo
        ? `${hourFrom} - ${hourTo}`
        : "N/D".localized();
    const expectedCount = Number.isFinite(row.expectedCount) ? row.expectedCount : 0;
    const checkinCount = Number.isFinite(row.checkinCount) ? row.checkinCount : 0;
    const formattedDate = row.timestamp
        ? Description.Timestamp.timeAgoWithDate(row.timestamp, timezone)
        : row.day;

    if (isKioskEvidenceSpoofAlert(row.alertType)) {
        return {
            id: safeText(row.id, `${buildingName}-kiosk-${safeText(row.day, "alert")}`),
            description: safeText(row.shortDescription || row.emailTitle, "Sin descripción".localized()),
            typeLabel: typeToLabel(row.alertType),
            importanceLabel: importanceToLabel(row.alertImportance),
            buildingName,
            shiftLabel: kioskEvidenceSpoofShiftLabel(),
            scheduleLabel: notApplicableLabel,
            countLabel: notApplicableLabel,
            dateLabel: safeText(formattedDate),
            row,
        };
    }

    return {
        id: safeText(row.id, `${buildingName}-${shiftLabel}-${safeText(row.day, "alert")}`),
        description: safeText(row.shortDescription || row.emailTitle, "Sin descripción".localized()),
        typeLabel: typeToLabel(row.alertType),
        importanceLabel: importanceToLabel(row.alertImportance),
        buildingName,
        shiftLabel,
        scheduleLabel,
        countLabel: `${checkinCount} / ${expectedCount}`,
        dateLabel: safeText(formattedDate),
        row,
    };
};

export const useAlertasListViewModel = ({
    orgID,
    timezone,
    service,
}: UseAlertasListViewModelProps) => {
    const [rows, setRows] = useState<AlertasListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchField, setSearchField] = useState("");
    const [selectedDate, setSelectedDate] = useState<Date>(() => new Date());
    const [selectedRow, setSelectedRow] = useState<AlertasListItem | null>(null);
    const [selectedDetail, setSelectedDetail] = useState<AlertDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);

    const getDayFromDate = useCallback((date: Date) => {
        return Utils.getDayStringFromTimestamp(date.getTime(), timezone);
    }, [timezone]);

    const day = useMemo(() => getDayFromDate(selectedDate), [getDayFromDate, selectedDate]);

    const fetchRows = useCallback(async () => {
        if (!orgID) {
            setRows([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await service.getAlertsTableForOrganizationDay(orgID, day);
            setRows(response.map((row) => mapAlertRow(row, timezone)));
        } catch {
            setError("No se pudieron cargar las alertas.".localized());
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [day, orgID, service, timezone]);

    useEffect(() => {
        void fetchRows();
    }, [fetchRows]);

    const openAlertDetail = useCallback(async (row: AlertasListItem) => {
        setSelectedRow(row);
        setSelectedDetail(null);
        setDetailError(null);
        setDetailLoading(true);

        try {
            const detail = await service.getAlertDetail(orgID, row.row.id ?? row.id);
            setSelectedDetail(detail);
        } catch {
            setDetailError("No se pudo cargar el detalle de la alerta.".localized());
        } finally {
            setDetailLoading(false);
        }
    }, [orgID, service]);

    const closeAlertDetail = useCallback(() => {
        setSelectedRow(null);
        setSelectedDetail(null);
        setDetailError(null);
        setDetailLoading(false);
    }, []);

    const filteredRows = useMemo(() => {
        const normalizedSearch = normalizeSearch(searchField);

        if (!normalizedSearch) {
            return rows;
        }

        return rows.filter((item) => {
            const searchableText = [
                item.description,
                item.typeLabel,
                item.importanceLabel,
                item.buildingName,
                item.shiftLabel,
                item.scheduleLabel,
                item.row.day,
            ].join(" ");

            return normalizeSearch(searchableText).includes(normalizedSearch);
        });
    }, [rows, searchField]);

    const formattedDay = useMemo(() => {
        return selectedDate.toLocaleDateString("es-AR", {
            timeZone: timezone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    }, [selectedDate, timezone]);

    return {
        day,
        error,
        formattedDay,
        getDayFromDate,
        loading,
        selectedDetail,
        selectedRow,
        detailError,
        detailLoading,
        rows: filteredRows,
        searchField,
        selectedDate,
        setSearchField,
        setSelectedDate,
        openAlertDetail,
        closeAlertDetail,
        refresh: fetchRows,
    };
};
